import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Star, ShieldAlert, MessageSquareText } from "lucide-react";
import { api } from "@/context/AuthContext";
import { DashboardSidebar } from "@/components/DashboardSidebar";
import { FullScreenLoader } from "@/components/FullScreenLoader";

const USE_AGAIN_LABEL = {
  definitely: "Definitely",
  maybe: "Maybe",
  probably_not: "Probably Not",
};

const USE_AGAIN_STYLE = {
  definitely: "border-[#22C55E]/30 bg-[#22C55E]/10 text-[#22C55E]",
  maybe: "border-[#F5A524]/30 bg-[#F5A524]/10 text-[#F5A524]",
  probably_not: "border-[#E52525]/30 bg-[#E52525]/10 text-[#E52525]",
};

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function Stars({ rating }) {
  return (
    <span className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          size={14}
          className={
            n <= rating
              ? "fill-[#E52525] text-[#E52525]"
              : "fill-transparent text-white/20"
          }
        />
      ))}
    </span>
  );
}

export default function AdminFeedback() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("loading"); // loading|denied|ok
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/admin/feedback");
        if (!alive) return;
        setData(r.data);
        setStatus("ok");
      } catch (err) {
        if (!alive) return;
        setStatus(err?.response?.status === 403 ? "denied" : "denied");
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  if (status === "loading") return <FullScreenLoader />;

  return (
    <div className="flex min-h-screen bg-[#0A0A0A] font-body text-white">
      <DashboardSidebar active="admin-feedback" />

      <main
        className="flex-1 px-6 py-7 md:px-12 md:py-10"
        data-testid="admin-feedback-page"
      >
        <div className="mx-auto max-w-6xl">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="mb-1 text-xs font-semibold uppercase tracking-[0.18em] text-[#E52525]">
                Admin
              </p>
              <h1 className="font-heading text-3xl font-black tracking-tight sm:text-4xl">
                Feedback
              </h1>
              <p className="mt-2 text-sm text-zinc-400">
                Every response from the post-session feedback modal.
              </p>
            </div>
          </div>

          {/* Sub-nav: Users / Feedback */}
          <div className="mt-6 inline-flex rounded-full border border-white/10 bg-[#141414] p-1 text-sm">
            <button
              type="button"
              data-testid="admin-tab-users"
              onClick={() => navigate("/admin/users")}
              className="rounded-full px-4 py-1.5 font-medium text-zinc-400 hover:text-white"
            >
              Users
            </button>
            <button
              type="button"
              data-testid="admin-tab-feedback"
              className="rounded-full bg-white/10 px-4 py-1.5 font-semibold text-white"
            >
              Feedback
            </button>
          </div>

          {status === "denied" && (
            <div
              data-testid="admin-denied"
              className="mt-10 flex items-start gap-3 rounded-2xl border border-[#E52525]/30 bg-[#E52525]/5 p-5"
            >
              <ShieldAlert size={20} className="mt-0.5 shrink-0 text-[#E52525]" />
              <div>
                <p className="font-semibold text-white">Admin access required</p>
                <p className="mt-1 text-sm text-zinc-400">
                  Your account can't view this page.
                </p>
                <button
                  onClick={() => navigate("/homepage")}
                  className="mt-3 rounded-full border border-white/15 bg-white/5 px-4 py-2 text-sm font-semibold text-white hover:bg-white/10"
                >
                  Back home
                </button>
              </div>
            </div>
          )}

          {status === "ok" && data && (
            <>
              {/* Stats */}
              <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div
                  data-testid="admin-avg-rating"
                  className="rounded-2xl border border-white/10 bg-[#141414] p-5"
                >
                  <p className="text-xs font-semibold uppercase tracking-[0.14em] text-zinc-500">
                    Average rating
                  </p>
                  <div className="mt-3 flex items-baseline gap-2">
                    <span className="font-heading text-4xl font-black tracking-tight text-white">
                      {data.total ? data.average_rating.toFixed(2) : "—"}
                    </span>
                    <span className="text-sm text-zinc-500">/ 5</span>
                  </div>
                  {data.total > 0 && (
                    <div className="mt-2">
                      <Stars rating={Math.round(data.average_rating)} />
                    </div>
                  )}
                </div>

                <div
                  data-testid="admin-total-responses"
                  className="rounded-2xl border border-white/10 bg-[#141414] p-5"
                >
                  <p className="text-xs font-semibold uppercase tracking-[0.14em] text-zinc-500">
                    Total responses
                  </p>
                  <div className="mt-3 flex items-baseline gap-2">
                    <span className="font-heading text-4xl font-black tracking-tight text-white">
                      {data.total}
                    </span>
                    <span className="text-sm text-zinc-500">
                      {data.total === 1 ? "response" : "responses"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Table */}
              <div className="mt-8 overflow-hidden rounded-2xl border border-white/10 bg-[#141414]">
                {data.items.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <MessageSquareText size={28} className="text-zinc-600" />
                    <p className="mt-3 text-sm text-zinc-400">
                      No feedback yet. Once reps start finishing calls, their
                      responses will show up here.
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table
                      data-testid="admin-feedback-table"
                      className="w-full min-w-[720px] text-sm"
                    >
                      <thead className="bg-white/5 text-xs uppercase tracking-[0.14em] text-zinc-500">
                        <tr>
                          <th className="px-5 py-3.5 text-left font-semibold">User</th>
                          <th className="px-5 py-3.5 text-left font-semibold">Rating</th>
                          <th className="px-5 py-3.5 text-left font-semibold">
                            Would use again
                          </th>
                          <th className="px-5 py-3.5 text-left font-semibold">
                            Comment
                          </th>
                          <th className="px-5 py-3.5 text-left font-semibold">
                            Submitted
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.items.map((f) => (
                          <tr
                            key={f.id}
                            className="border-t border-white/5 align-top hover:bg-white/[0.02]"
                          >
                            <td className="px-5 py-4">
                              <div className="flex flex-col">
                                <span className="font-medium text-white">
                                  {f.user?.name || "—"}
                                </span>
                                <span className="text-xs text-zinc-500">
                                  {f.user?.username
                                    ? `@${f.user.username}`
                                    : f.user?.email || ""}
                                </span>
                              </div>
                            </td>
                            <td className="px-5 py-4">
                              <div className="flex items-center gap-2">
                                <Stars rating={f.rating} />
                                <span className="text-xs text-zinc-500">
                                  {f.rating}/5
                                </span>
                              </div>
                            </td>
                            <td className="px-5 py-4">
                              <span
                                className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${
                                  USE_AGAIN_STYLE[f.would_use_again] ||
                                  "border-white/10 bg-transparent text-zinc-400"
                                }`}
                              >
                                {USE_AGAIN_LABEL[f.would_use_again] ||
                                  f.would_use_again}
                              </span>
                            </td>
                            <td className="max-w-xs px-5 py-4">
                              <p className="whitespace-pre-wrap text-sm text-zinc-300">
                                {f.comment || (
                                  <span className="text-zinc-600">—</span>
                                )}
                              </p>
                            </td>
                            <td className="whitespace-nowrap px-5 py-4 text-xs text-zinc-500">
                              {formatDate(f.created_at)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
